import { createSectionName } from 'components/Dashboard/helpers/controlActiveFilters';
import { contentItemSearchQuery } from 'components/Dashboard/helpers/locationSearchDescriptor';
import { useMainSectionParameter } from 'components/Dashboard/helpers/useMainSectionParameter';
import { unseenNumbers } from 'components/Dashboard/hooks/useRememberPosition';
import getSearchWithinHash from 'components/Dashboard/utilities/getSearchWithinHash';
import getUrlParam from 'components/Dashboard/utilities/getUrlParam';
import { store } from 'components/Store';
import { useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { useContentResults } from 'components/Dashboard/ContentResultsContext';
import useDetectRateMode from 'components/Dashboard/ListControl/useDetectRateMode';

const useItemController = (props) => {
  const {
    item,
    index,
    propsFeedItemFlags,
    itemFromWidget,
    hideYellowDot,
    listView = true,
    thumbDownProps,
    showPriorityBadge,
    itemName,
  } = props;
  const globalState = useContext(store);
  const { state } = globalState;
  const { search, referenceState } = state;
  const { contentResults, lastSeenMarkers, itemsViewed } = useContentResults();
  const { activeReference } = referenceState;

  const sectionName = createSectionName();
  const location = useLocation();
  const { rateUpdatesMode } = useDetectRateMode();

  const { list } = search;
  const section = contentResults[sectionName];
  const feedItemFlags = propsFeedItemFlags ?? section?.feedItemFlags ?? [];

  const { inboxSection, referenceSection, dashboardSection } = useMainSectionParameter();

  const locationDescriptor = (path, hash) => {
    let pathname = location.pathname;
    let locationSearch = contentItemSearchQuery(location, list);
    const sectionNameToUse = itemName ?? sectionName;
    if (
      (getUrlParam('topic-id') === 'all' && (inboxSection || pathname.startsWith('/stakeholders'))) ||
      dashboardSection
    ) {
      const matchingListId = feedItemFlags?.[index]?.matchingKeywordLists?.[0]?.id;
      if (matchingListId) {
        locationSearch = `?topic-id=${matchingListId}`;
      }
      if (sectionNameToUse === 'KeyUpdate') {
        locationSearch += '&stakeholder-topic=false';
      } else if (sectionNameToUse === 'ParliamentaryRecord') {
        let hansardItem = item.contentType === 'ParliamentaryContribution';
        let welshItem = item.contentType === 'WalesCountrySpecificParliamentaryContribution';
        let scottishItem = item.contentType === 'ScotlandCountrySpecificParliamentaryContribution';
        let seneddCommitteeTranscriptItem = item.contentType === 'SeneddCommitteeTranscriptContribution';
        if (
          pathname.startsWith('/stakeholders') &&
          (hansardItem || welshItem || scottishItem || seneddCommitteeTranscriptItem)
        ) {
          let topicParam = getUrlParam('topic-id');
          if (!topicParam) {
            locationSearch = `?topic-id=none`;
          }
          locationSearch += `&contact-id=${pathname.match(/[0-9]+/)[0]}`;
        }

        if (referenceSection && !activeReference && hansardItem) {
          locationSearch = `?topic-id=none`;
        }
      }
    }

    const hashToRender = `${!!hash ? hash : ''}${
      getSearchWithinHash() ? `~search-within:${encodeURIComponent(getSearchWithinHash())}` : ''
    }`;
    return {
      pathname: path,
      search: `${locationSearch}`,
      hash: hashToRender,
    };
  };

  const lastSeenMarkerName = unseenNumbers[sectionName]?.lastSeenMarker;

  const feedItemsConditional = list !== null && feedItemFlags?.length > 0;
  const feedItem = feedItemFlags?.[index];
  const isBackpopulatedCondition = feedItem?.isBackpopulated === null || feedItem?.isBackpopulated === true;
  const unseenConditional =
    feedItemsConditional &&
    !!lastSeenMarkers[lastSeenMarkerName] &&
    lastSeenMarkers[lastSeenMarkerName] < feedItem?.feedItemId &&
    !itemsViewed.includes(item?.objectID) &&
    !isBackpopulatedCondition &&
    listView &&
    !itemFromWidget &&
    !hideYellowDot;

  const alreadyMarkedFeedback = thumbDownProps?.alreadyMarked ?? feedItem?.ratingScored === 'Negative';

  const priorityBadgeCondition =
    showPriorityBadge &&
    feedItem?.relevanceScore >= (import.meta.env.VITE_APP_PRIORITY_UPDATES_MIN_RELEVANCE_SCORE ?? 0.8) &&
    feedItem?.importanceScore >= 0;

  return { unseenConditional, locationDescriptor, alreadyMarkedFeedback, priorityBadgeCondition, rateUpdatesMode };
};

export default useItemController;
